// ScenarioCard.tsx - Card component for grid view

import React from 'react';
import { Scenario } from '../../types/ScenarioManagement';
import { useThemeUtils } from '../../hooks/useThemeUtils';
// Removed unused useTheme import
import { StandardCard, StandardButton } from '../ui';

interface ScenarioCardProps {
  scenario: Scenario;
  onEdit: () => void;
  onDelete: () => void;
}

const ScenarioCard: React.FC<ScenarioCardProps> = React.memo(({
  scenario,
  onEdit,
  onDelete
}) => {
  const { textPatterns, themeClass } = useThemeUtils();
  // Removed unused isDark from useTheme

  return (
    <StandardCard
      variant="elevated"
      hoverable
      padding="large"
      className={`relative ${scenario.deletedAt ? 'opacity-60' : ''}`}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -4 }}
    >

      {/* Actions Menu */}
      <div className="absolute top-4 right-4 flex gap-1">
        <StandardButton
          onClick={onEdit}
          variant="ghost"
          size="small"
          className="!p-1.5"
          title="Edit scenario"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
          </svg>
        </StandardButton>
        <StandardButton
          onClick={onDelete}
          variant="ghost"
          size="small"
          className="!p-1.5 !text-red-400 hover:!text-red-300"
          title="Delete scenario"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
        </StandardButton>
      </div>

      {/* Deleted Badge */}
      {scenario.deletedAt && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-12">
          <span className={themeClass({
            base: 'px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wider',
            light: 'bg-red-100 text-red-700 border border-red-200',
            dark: 'bg-red-900/30 text-red-300 border border-red-700'
          })}>
            Deleted
          </span>
        </div>
      )}

      {/* Icon */}
      <div className="flex items-start justify-between mb-4 mt-8">
        <div className="text-4xl">{scenario.icon}</div>
      </div>

      {/* Title and Subtitle */}
      <div className="mb-3">
        <h3 className={`text-lg font-semibold ${textPatterns.primary} mb-1 flex items-center gap-2`}>
          {scenario.title}
          {scenario.id === 'techcorp' && (
            <span className={themeClass({
              base: 'text-xs px-2 py-0.5 rounded-full font-medium',
              light: 'bg-professional-blue-100 text-professional-blue-700 border border-professional-blue-200',
              dark: 'bg-professional-blue-900/30 text-professional-blue-300 border border-professional-blue-700'
            })}>
              Main Scenario
            </span>
          )}
        </h3>
        <p className={`text-sm ${themeClass({ light: 'text-professional-blue-600', dark: 'text-professional-blue-300' })}`}>
          {scenario.subtitle}
        </p>
      </div>

      {/* Description */}
      <p className={`text-sm ${textPatterns.secondary} mb-4`}>
        {scenario.description}
      </p>


      {/* Footer */}
      <div className={`flex items-center justify-between text-xs ${textPatterns.tertiary} pt-4`}>
        <div className="flex items-center gap-3">
          {/* Removed duration and playCount display */}
        </div>
        {!scenario.isAvailable && (
          <span className={`font-medium ${textPatterns.tertiary}`}>
            Coming Soon
          </span>
        )}
      </div>
    </StandardCard>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  // Only re-render if scenario data or callbacks change
  return (
    prevProps.scenario.id === nextProps.scenario.id &&
    prevProps.scenario.title === nextProps.scenario.title &&
    prevProps.scenario.subtitle === nextProps.scenario.subtitle &&
    prevProps.scenario.description === nextProps.scenario.description &&
    prevProps.scenario.icon === nextProps.scenario.icon &&
    prevProps.scenario.isAvailable === nextProps.scenario.isAvailable &&
    prevProps.scenario.deletedAt === nextProps.scenario.deletedAt &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete
  );
});

ScenarioCard.displayName = 'ScenarioCard';

export default ScenarioCard;